import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import UserApi from '../API/UserApi';
import { useAuth } from '../Auth/AuthProvider';
import { Avatar } from '../Components/Avatar';
import { CoinsPill } from '../Components/CoinsPill';
import DropdownMenu from '../Components/DropdownMenu';
import { NotificationIcon } from '../Components/NotificationIcon';
import { XPPill } from '../Components/XPPill';
import { useAchievements } from '../Context/AchievementsProvider';
import { AnalyticsEvent, useAnalytics } from '../Context/AnalyticsProvider';
import { useBalance } from '../Context/BalanceProvider';
import { useFeedback } from '../Context/FeedbackProvider';
import { useStreak } from '../Context/StreakProvider';
import { useUISettings } from '../Context/UISettingsProvider';
import Modal from '../Modal/Modal';
import SwitchAccounts from '../ParentDashboard/SwitchAccountsView';
import EditProfile from '../Profile/EditProfile';
import { useNavigate } from '../ProtectedRoute';
import { User, UserType } from '../Types/User';
import './AppHeader.css';

const { slide } = require('react-burger-menu'); // The types that are available for this lib are defective and fail build
const Menu = slide;

function AppHeader() {
  const {
    isAuthenticated,
    user,
    parentUser,
    studentUser,
    authenticatedUser,
    impersonate,
    signOut,
  } = useAuth();
  const { streak, showStreakModal } = useStreak();
  const { trackEvent } = useAnalytics();
  const [parentUserDetails, setParentUserDetails] = useState<User>();
  const [studentUserDetails, setStudentUserDetails] = useState<User>();
  const { achievements } = useAchievements();
  const { isFeedbackProviderInitialized, showFeedbackModal } = useFeedback();

  const navigate = useNavigate();
  const { showHUD } = useUISettings();
  const { isLoadingBalances } = useBalance();
  const [isEditProfileOpen, setIsEditProfileOpen] = useState(false);
  const [isSwitchAccountsOpen, setIsSwitchAccountsOpen] = useState(false);
  const profileImageRef = useRef<HTMLImageElement>(null);

  const userDetails = useMemo(() => {
    return studentUserDetails ?? parentUserDetails;
  }, [parentUserDetails, studentUserDetails]);

  useEffect(() => {
    const userApi = new UserApi();
    if (parentUser) {
      userApi
        .getUser(parentUser.id)
        .then((user) => {
          setParentUserDetails(user);
          setStudentUserDetails(
            studentUser
              ? user.children?.find((u) => u.id === studentUser.id)
              : null,
          );
        })
        .catch((ex) => {
          console.error('Failed to fetch parent user', ex);
        });
    } else if (studentUser) {
      userApi
        .getUser(studentUser.id)
        .then((user) => {
          setParentUserDetails(null);
          setStudentUserDetails(user);
        })
        .catch((ex) => {
          console.error('Failed to fetch student user', ex);
        });
    }
  }, [parentUser, studentUser]);

  const HeaderButton = ({
    href,
    onClick,
    name,
    authLocked,
    altPaths = undefined,
    testId,
  }: {
    href?: string;
    onClick?: () => Promise<void>;
    name: string;
    authLocked?: boolean;
    altPaths?: string[];
    testId: string;
  }) => {
    const [isInProgress, setIsInProgress] = useState(false);

    const isActive =
      (href && window.location.pathname.startsWith(href)) ||
      altPaths?.includes(window.location.pathname);
    const isVisible = !(authLocked ?? true) || isAuthenticated;

    return (
      <>
        {isVisible ? (
          <a
            href={href ?? 'javascript:void(0);'}
            data-testid={testId}
            className={`px-4 py-2 ${
              isActive ? 'bg-[#FFF3]' : 'lg:bg-[#FFFFFF18]'
            } rounded-[100px] max-lg:rounded-[6px] justify-start items-center gap-1.5 flex `}
            onClick={async (e) => {
              if (isInProgress) return;
              try {
                setIsInProgress(true);
                await onClick?.();
              } finally {
                setIsInProgress(false);
              }
              if (href) {
                navigate(href);
              }
              e.preventDefault();
            }}
          >
            <span
              className={`
                text-base ${isActive ? 'font-bold text-gray-200' : ''}
                text-gray-200
                ${isInProgress ? 'opacity-50' : ''}
              `}
            >
              {name}
              {isInProgress && (
                <FontAwesomeIcon
                  icon={faSpinner}
                  className="ml-2 animate-spin"
                />
              )}
            </span>
          </a>
        ) : (
          ''
        )}
      </>
    );
  };

  const Navigation = () => {
    if (!user?.type) return null;

    switch (user.type) {
      case UserType.Student:
        return (
          <>
            <HeaderButton
              testId="create-my-own-story-link"
              href="/create-my-own-story"
              name="Create My Own Story"
              altPaths={['/']}
            />
            <HeaderButton
              testId="my-stories-link"
              href="/my-stories"
              name="My Stories"
            />
            <HeaderButton
              testId="library-link"
              href="/library"
              name="Library"
            />
            {parentUser ? (
              <HeaderButton
                testId="reading-circles-link"
                href="/reading-circles"
                name="My Reading Circles"
              />
            ) : null}
          </>
        );
      case UserType.Parent:
        return (
          <>
            <HeaderButton
              testId="my-accounts-link"
              href="/parent-dashboard"
              name="My Accounts"
              altPaths={['/']}
            />
            <HeaderButton
              testId="reading-circles-link"
              href="/reading-circles"
              name="My Reading Circles"
            />
          </>
        );
      default:
        console.warn('Can not show menu for unknown user type:', user.type);
        return null;
    }
  };

  if (!showHUD) return null;

  return (
    <>
      <header
        className={`
        flex items-center w-full px-6 py-2.5 bg-blue-600
        max-sm:py-4
      `}
      >
        <div className="flex flex-1 justify-start">
          {/* Logo and Title */}
          <div
            className="flex gap-1.5 items-center cursor-pointer"
            onClick={(e) => {
              navigate('/');
              trackEvent(AnalyticsEvent.HEADER_LOGO_CLICKED, {
                referrer:
                  document.referrer ?? window.frames.top?.document.referrer,
              });
              e.preventDefault();
            }}
          >
            <div className="bg-white flex aspect-square px-1.5 w-7 h-7 rounded-[100px]">
              <img
                loading="lazy"
                src={require('../assets/logo-inverted.svg')}
                className="w-full fill-blue-200 overflow-hidden"
                alt="TeachTales Logo"
                aria-label="TeachTales Logo"
              />
            </div>
            <div
              className={`
              text-base font-semibold self-center grow whitespace-nowrap flex
              text-gray-200
            `}
            >
              TeachTales
            </div>
          </div>
        </div>
        <div
          className="flex justify-center flex-grow gap-1.5 max-lg:hidden"
          data-testid="header-navigation"
        >
          <Navigation />
        </div>
        <div className="flex flex-1 justify-end">
          {userDetails ? (
            <div className="flex flex-row items-center gap-2 text-white">
              {user?.type === UserType.Student ? (
                <div
                  className="h-9 max-sm:h-7 p-2 cursor-pointer bg-[#FFE5B9] rounded-full border border-[#FF6723] border-opacity-20 justify-start items-center inline-flex"
                  onClick={() =>
                    showStreakModal(
                      !streak?.currentStreakLength
                        ? '<b>Start your streak</b>'
                        : `<b>${streak.currentStreakLength}</b>-Day streak`,
                    )
                  }
                >
                  <div className="relative w-4 h-4">
                    <img
                      loading="lazy"
                      src={require('../assets/flame_streak.svg')}
                      alt="Streak"
                      className="w-full h-full aspect-square"
                    />
                  </div>
                  <div className="text-center text-[#FF6723] text-base font-bold">
                    {streak?.currentStreakLength ?? 0}
                  </div>
                </div>
              ) : null}
              {user?.type === UserType.Student && !isLoadingBalances ? (
                <>
                  <XPPill />
                  <CoinsPill />
                </>
              ) : null}
              {user?.type === UserType.Student ? <NotificationIcon /> : null}
              <div
                className="relative max-lg:hidden"
                data-testid="profile-image-container"
              >
                <div
                  ref={profileImageRef}
                  className="flex flex-row items-center gap-2 cursor-pointer"
                >
                  <div className="underline">{userDetails.name}</div>
                  <Avatar
                    user={userDetails}
                    className="shrink-0 w-10 aspect-square rounded-full cursor-pointer"
                  />
                </div>
                <DropdownMenu
                  triggerRef={profileImageRef}
                  options={[
                    {
                      title: 'Edit Profile',
                      onClick: async () => {
                        setIsEditProfileOpen(true);
                      },
                    },
                    ...(authenticatedUser?.type === UserType.Parent
                      ? [
                          {
                            title: 'Switch Accounts',
                            onClick: async () => {
                              setIsSwitchAccountsOpen(true);
                            },
                          },
                        ]
                      : []),
                    ...(parentUser && !studentUser
                      ? [
                          {
                            title: 'Manage Billing',
                            onClick: async () => {
                              await impersonate(parentUserDetails);
                              window.open(
                                window.env.REACT_APP_BILLING_URL,
                                '_blank', // On Web it will open in a new tab, On Mobile, it will still open in in-app browser as required by Apple
                              );
                            },
                          },
                        ]
                      : []),
                    {
                      title: 'Log Out',
                      onClick: async () => {
                        await signOut();
                      },
                      className: 'text-red-600',
                    },
                  ]}
                />
              </div>
              <span className="hidden max-lg:inline ml-3">
                <Menu right>
                  <section className="flex flex-col  w-full text-center">
                    <div className="flex justify-center w-full mb-3">
                      <Avatar
                        user={userDetails}
                        size={80}
                        onClick={() => setIsEditProfileOpen(true)}
                      />
                    </div>
                    <p>
                      {userDetails.name} {userDetails.surname}
                    </p>
                  </section>
                  {parentUser ? (
                    <HeaderButton
                      testId="switch-accounts-link"
                      name="Switch Accounts"
                      onClick={async () => {
                        setIsSwitchAccountsOpen(true);
                      }}
                    />
                  ) : null}
                  <hr />
                  <div data-testid="menu-navigation">
                    <Navigation />
                    {parentUser && !studentUser ? (
                      <HeaderButton
                        testId="manage-billing-link"
                        name="Manage Billing"
                        onClick={async () => {
                          await impersonate(parentUserDetails);
                          window.open(
                            window.env.REACT_APP_BILLING_URL,
                            '_blank', // On Web it will open in a new tab, On Mobile, it will still open in in-app browser as required by Apple
                          );
                        }}
                      />
                    ) : null}
                  </div>
                  {isFeedbackProviderInitialized && (
                    <>
                      <hr />
                      <div
                        onClick={showFeedbackModal}
                        className="px-4 py-2 lg:bg-[#FFFFFF18] rounded-[100px] max-lg:rounded-[6px] justify-start items-center gap-1.5 flex cursor-pointer"
                      >
                        <span className="text-base text-gray-200">
                          Feedback
                        </span>
                      </div>
                    </>
                  )}
                  <hr />
                  <HeaderButton
                    testId="logout-link"
                    name="Logout"
                    onClick={signOut}
                  />
                </Menu>
              </span>
            </div>
          ) : null}
        </div>
      </header>
      {isEditProfileOpen && user ? (
        <Modal isOpen={isEditProfileOpen}>
          <EditProfile onClose={() => setIsEditProfileOpen(false)} />
        </Modal>
      ) : null}
      {isSwitchAccountsOpen ? (
        <Modal isOpen={isSwitchAccountsOpen}>
          <SwitchAccounts
            parentUser={parentUserDetails}
            studentUsers={parentUserDetails?.children ?? []}
            onSwitchRequested={async (user) => {
              await impersonate(user);
              setIsSwitchAccountsOpen(false);
              navigate('/');
            }}
            onCancel={() => setIsSwitchAccountsOpen(false)}
          />
        </Modal>
      ) : null}
    </>
  );
}

export default AppHeader;
